import throttle from "lodash/throttle";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useRef } from "react";

/** hooks */
import { useChannel } from "@/components/Messaging/useChannel";
import { useChatWidgetContext } from "@/components/Playground/Drawer/Chat/context";
import { useUser } from "@/context/user-context";
/** constants */
import { WEBSOCKET_MESSAGE_NAME } from "@/constants";
/** types */
import { ChatMessageI } from "@/components/Playground/types";
import { Content } from "@/types";
/** components */
import { ChatMessage } from "@/components/Playground/Drawer/Chat/ChatMessage";
import { ChatEmptyState } from "@/components/Playground/Drawer/Chat/ChatPanel/ChatEmptyState";
import { LoadingMoreSpinner } from "@/components/Playground/Drawer/Chat/ChatPanel/LoadingMoreSpinner";
import { LoadingShimmer } from "@/components/Playground/Drawer/Chat/ChatPanel/LoadingShimmer";
import { LoadMoreMessagesSkeleton } from "@/components/Playground/Drawer/Chat/ChatSkeleton";

interface ChatPanelProps {
  messages: ChatMessageI[];
  isLoading?: boolean;
  isStreaming?: boolean;
  isFetchingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditMessage?: (messageId: string, newContent: Content[]) => void;
  onDeleteMessage?: (messageId: string) => void;
  onReplayMessage?: (messageId: string) => void;
}

const SCROLL_THRESHOLD = 20;

const scrollToBottom = (element: HTMLDivElement, smooth = true) => {
  element.scrollTo({
    top: element.scrollHeight,
    behavior: smooth ? "smooth" : "instant",
  });
};

const isNearBottom = (element: HTMLDivElement) => {
  const { scrollTop, scrollHeight, clientHeight } = element;
  return scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD;
};

export const ChatPanel = observer(
  ({
    messages,
    isLoading = false,
    isStreaming = false,
    isFetchingMore = false,
    hasMore = false,
    onLoadMore,
    onEditMessage,
    onDeleteMessage,
    onReplayMessage,
  }: ChatPanelProps) => {
    const { user } = useUser();
    const scrollRef = useRef<HTMLDivElement>(null);
    const isInitializingRef = useRef(true);
    const initializationTimeoutRef = useRef<NodeJS.Timeout>();
    const prevMessageCount = useRef(messages.length);
    const prevLastMessageId = useRef(messages[messages.length - 1]?.id);
    const isStuckToBottomRef = useRef(true);
    const { isContainerSizeSmall, variableSetIndex } = useChatWidgetContext();

    const handleScrollToBottom = (delay?: number) => () => {
      setTimeout(() => {
        if (!scrollRef.current) {
          return;
        }
        if (!isStuckToBottomRef.current) {
          return;
        }
        scrollToBottom(scrollRef.current, false);
      }, delay ?? 0);
    };

    // Handle scroll to bottom during initialization and for new messages
    useEffect(() => {
      if (!scrollRef.current || messages.length === 0) {
        return;
      }

      if (isInitializingRef.current) {
        // Reset stability timer on each message update during init
        if (initializationTimeoutRef.current) {
          clearTimeout(initializationTimeoutRef.current);
        }

        scrollToBottom(scrollRef.current, false);

        // End initialization after stable period (no new messages)
        initializationTimeoutRef.current = setTimeout(() => {
          isInitializingRef.current = false;
        }, 300);
      } else if (messages.length > prevMessageCount.current) {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage && lastMessage.id !== prevLastMessageId.current) {
          // New message added - reset sticky state and scroll to bottom
          isStuckToBottomRef.current = true;
          scrollToBottom(scrollRef.current);
        }
      }

      prevMessageCount.current = messages.length;
      prevLastMessageId.current = messages[messages.length - 1]?.id;
    }, [messages]);

    useChannel(
      `user:${user.id}`,
      WEBSOCKET_MESSAGE_NAME.UPDATE_LAST_MESSAGE,
      handleScrollToBottom(),
    );
    useChannel(
      `user:${user.id}`,
      WEBSOCKET_MESSAGE_NAME.INDIVIDUAL_RUN_COMPLETE,
      handleScrollToBottom(500),
    );

    // Keep scrolled to bottom during streaming (respects user opt-out)
    useEffect(() => {
      if (!scrollRef.current || !isStreaming) {
        return;
      }

      const scrollInterval = setInterval(() => {
        if (scrollRef.current && isStuckToBottomRef.current) {
          scrollToBottom(scrollRef.current, false);
        }
      }, 100);

      return () => clearInterval(scrollInterval);
    }, [isStreaming]);

    const handleLoadMoreCheck = useCallback(() => {
      if (isInitializingRef.current || !scrollRef.current) {
        return;
      }

      // Pagination: load more when near top
      if (!hasMore || isFetchingMore || !onLoadMore) {
        return;
      }

      const { scrollTop } = scrollRef.current;

      if (scrollTop < SCROLL_THRESHOLD) {
        onLoadMore();
      }
    }, [hasMore, isFetchingMore, onLoadMore]);

    const throttledLoadMoreCheck = useMemo(
      () => throttle(handleLoadMoreCheck, 150, { leading: true, trailing: true }),
      [handleLoadMoreCheck],
    );

    const handleScroll = useCallback(() => {
      if (!scrollRef.current || isInitializingRef.current) {
        return;
      }

      // Track sticky state immediately
      isStuckToBottomRef.current = isNearBottom(scrollRef.current);

      throttledLoadMoreCheck();
    }, [throttledLoadMoreCheck]);

    useEffect(() => () => throttledLoadMoreCheck.cancel(), [throttledLoadMoreCheck]);

    useEffect(
      () => () => {
        if (initializationTimeoutRef.current) {
          clearTimeout(initializationTimeoutRef.current);
        }
      },
      [],
    );

    if (messages.length === 0 && !isLoading) {
      return <ChatEmptyState />;
    }
    return (
      <div className="relative flex flex-1 flex-col overflow-hidden">
        <div className="pointer-events-none absolute left-0 right-0 top-0 z-10 h-8 bg-gradient-to-b from-white to-transparent" />
        {!isStreaming && isFetchingMore && <LoadingMoreSpinner />}

        <div
          ref={scrollRef}
          data-chat-scroll-container
          data-variable-set-index={variableSetIndex}
          className="flex flex-1 flex-col gap-y-2 overflow-y-auto px-3 pb-3 pt-3"
          onScroll={handleScroll}
        >
          <div className="flex flex-col gap-y-3">
            {!isStreaming && isFetchingMore && <LoadMoreMessagesSkeleton />}
            {messages.map((message, index) => (
              <ChatMessage
                key={`${message.id}`}
                message={message}
                messages={messages}
                isMinimal={isContainerSizeSmall}
                isStreaming={isStreaming && index === messages.length - 1}
                onEditMessage={onEditMessage}
                onDeleteMessage={onDeleteMessage}
                onReplayMessage={onReplayMessage}
              />
            ))}
          </div>

          {!isStreaming && !isFetchingMore && isLoading && messages.length === 0 && (
            <LoadMoreMessagesSkeleton />
          )}

          {!isStreaming && isLoading && <LoadingShimmer />}
        </div>
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent" />
      </div>
    );
  },
);
